import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaImage, FaVideo, FaUpload, FaCalendar, FaGlobe, FaEye, FaChartBar } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { festivalService } from '../../services/festivalService';
import { wishesService } from '../../services/wishesService';
import { languageService } from '../../services/languageService';
import FestivalForm from './FestivalForm';
// Using Tailwind CSS classes instead of external CSS

const FestivalManagement = () => {
  const { t } = useTranslation();
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFestival, setEditingFestival] = useState(null);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [selectedFestival, setSelectedFestival] = useState(null);
  const [uploadData, setUploadData] = useState({
    file: null,
    occasionName: '',
    dates: [''],
    category: 'Festival',
    tags: '',
    status: 'active'
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchFestivals();
  }, []);

  const fetchFestivals = async () => {
    try {
      setLoading(true);
      const response = await festivalService.getFestivals();
      console.log('=== fetchFestivals response ===');
      console.log('Response:', response);
      console.log('Response.data:', response.data);
      if (response.data && response.data.length > 0) {
        console.log('First festival name:', response.data[0].name);
        console.log('First festival name type:', typeof response.data[0].name);
      }
      setFestivals(response.data || []);
    } catch (error) {
      console.error('Error fetching festivals:', error);
      alert('Error loading festivals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditFestival = (festival) => {
    console.log('=== handleEditFestival ===');
    console.log('Full festival object:', festival);
    console.log('Festival name:', festival.name);
    console.log('Festival name type:', typeof festival.name);
    console.log('Festival name keys:', festival.name ? Object.keys(festival.name) : 'N/A');
    console.log('Festival description:', festival.description);
    console.log('Festival description keys:', festival.description ? Object.keys(festival.description) : 'N/A');
    setEditingFestival(festival);
    setShowForm(true);
  };

  const handleDeleteFestival = async (festivalId) => {
    if (window.confirm('Are you sure you want to delete this festival? This will also delete all associated wishes media.')) {
      try {
        await festivalService.deleteFestival(festivalId);
        fetchFestivals();
        alert('Festival deleted successfully');
      } catch (error) {
        console.error('Error deleting festival:', error);
        alert('Error deleting festival. Please try again.');
      }
    }
  };

  const handleAddMedia = (festival) => {
    setSelectedFestival(festival);
    setUploadData({
      ...uploadData,
      occasionName: Object.values(festival.name)[0] || '',
      dates: [new Date(festival.date).toISOString().split('T')[0]]
    });
    setShowMediaUpload(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type and size
      const maxSize = 100 * 1024 * 1024; // 100MB
      const allowedTypes = ['image/', 'video/'];
      
      if (file.size > maxSize) {
        alert('File size must be less than 100MB');
        return;
      }
      
      if (!allowedTypes.some(type => file.type.startsWith(type))) {
        alert('Only image and video files are allowed');
        return;
      }

      setUploadData(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !selectedFestival) {
      alert('Please select a file and festival');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('media', uploadData.file);
      formData.append('festivalId', selectedFestival._id);
      formData.append('occasionName', uploadData.occasionName);
      formData.append('dates', JSON.stringify(uploadData.dates));
      formData.append('category', uploadData.category);
      formData.append('tags', uploadData.tags);

      await wishesService.uploadWishesMedia(formData);
      
      alert('Media uploaded successfully!');
      setShowMediaUpload(false);
      setUploadData({
        file: null,
        occasionName: '',
        dates: [''],
        category: 'Festival',
        tags: '',
        status: 'active'
      });
      fetchFestivals(); // Refresh to show new media
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading media. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const getLanguageNames = (languages) => {
    return languages.join(', ');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading festivals...</p>
        </div>
      </div>
    );
  }

  // Calculate stats
  const totalImages = festivals.reduce((acc, festival) => acc + (festival.images?.length || 0), 0);
  const totalVideos = festivals.reduce((acc, festival) => acc + (festival.videos?.length || 0), 0);
  const totalWishes = festivals.reduce((acc, festival) => acc + (festival.wishesMedia?.length || 0), 0);
  const activeCategories = [...new Set(festivals.map(f => f.category))].length;

  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Story Management</h1>
          <p className="text-muted-foreground">Create and manage your story content library</p>
        </div>
        <button
          className="btn btn-default"
          onClick={() => {
            setEditingFestival(null);
            setShowForm(true);
          }}
        >
          <FaPlus className="mr-2 h-4 w-4" />
          Create Stories
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-header flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="card-title text-sm font-medium">Total Stories</div>
            <FaCalendar className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="card-content">
            <div className="text-2xl font-bold">{festivals.length}</div>
            <p className="text-xs text-muted-foreground">+12% from last month</p>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="card-title text-sm font-medium">Images</div>
            <FaImage className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="card-content">
            <div className="text-2xl font-bold">{totalImages}</div>
            <p className="text-xs text-muted-foreground">+8% from last month</p>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="card-title text-sm font-medium">Videos</div>
            <FaVideo className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="card-content">
            <div className="text-2xl font-bold">{totalVideos}</div>
            <p className="text-xs text-muted-foreground">+15% from last month</p>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="card-title text-sm font-medium">Wishes Created</div>
            <FaChartBar className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="card-content">
            <div className="text-2xl font-bold">{totalWishes}</div>
            <p className="text-xs text-muted-foreground">+23% from last month</p>
          </div>
        </div>
      </div>

      {/* Festivals Grid */}
      <div className="card mt-4">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="card-title">All Stories</div>
            <select className="select w-48">
              <option>All Categories</option>
              <option>Religious</option>
              <option>National</option>
              <option>Cultural</option>
            </select>
          </div>
        </div>
        <div className="card-content">

          {festivals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FaCalendar className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Story yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Create your first story to get started with content management</p>
              <button
                className="btn btn-default mt-6"
                onClick={() => {
                  setEditingFestival(null);
                  setShowForm(true);
                }}
              >
                <FaPlus className="mr-2 h-4 w-4" />
                Create Your First Story
              </button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {festivals.map((festival) => (
                <div key={festival._id} className="card hover:shadow-md transition-shadow">
                  <div className="card-header">
                    <div className="flex items-center justify-between">
                      <span className="badge badge-secondary">{festival.category}</span>
                      <div className="flex space-x-1">
                        <button
                          className="btn btn-ghost btn-icon"
                          onClick={() => handleEditFestival(festival)}
                          title="Edit Festival"
                        >
                          <FaEdit className="h-5 w-5" />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon"
                          onClick={() => handleAddMedia(festival)}
                          title="Add Media"
                        >
                          <FaUpload className="h-5 w-5" />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon text-destructive hover:text-destructive"
                          onClick={() => handleDeleteFestival(festival._id)}
                          title="Delete Festival"
                        >
                          <FaTrash className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="card-content">
                    <h3 className="font-semibold leading-none tracking-tight mb-3">
                      {Object.values(festival.name)[0] || 'Unnamed Festival'}
                    </h3>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <FaCalendar className="mr-2 h-3 w-3" />
                        <span>{formatDate(festival.date)}</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <FaGlobe className="mr-2 h-3 w-3" />
                        <span>{getLanguageNames(festival.languages)}</span>
                      </div>
                    </div>

                    {Object.values(festival.description || {})[0] && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {Object.values(festival.description)[0].substring(0, 120)}
                        {Object.values(festival.description)[0].length > 120 ? '...' : ''}
                      </p>
                    )}
                  </div>

                  <div className="card-footer">
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <FaImage className="mr-1 h-3 w-3" />
                          <span>{festival.images?.length || 0}</span>
                        </div>
                        <div className="flex items-center">
                          <FaVideo className="mr-1 h-3 w-3" />
                          <span>{festival.videos?.length || 0}</span>
                        </div>
                        {festival.wishesMedia && festival.wishesMedia.length > 0 && (
                          <div className="flex items-center">
                            <FaEye className="mr-1 h-3 w-3" />
                            <span>{festival.wishesMedia.length}</span>
                          </div>
                        )}
                      </div>
                      
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleEditFestival(festival)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Festival Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
          <div className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-6lg translate-x-[-50%] translate-y-[-50%] gap-4  bg-background p-6 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
            <FestivalForm
              festival={editingFestival}
              onClose={() => {
                setShowForm(false);
                setEditingFestival(null);
                fetchFestivals();
              }}
            />
          </div>
        </div>
      )}

      {/* Media Upload Modal */}
      {showMediaUpload && (
        <div className="fixed inset-0 z-50 bg-black/80">
          <div className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] border bg-background p-6 shadow-lg sm:rounded-lg">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <h3 className="text-lg font-semibold">Add Media to {selectedFestival?.name.en || 'Festival'}</h3>
              <button 
                className="btn btn-ghost btn-icon"
                onClick={() => setShowMediaUpload(false)}
              >
                ×
              </button>
            </div>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="label">Select Media File</label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="input"
                />
                {uploadData.file && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {uploadData.file.name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="label">Occasion Name</label>
                <input
                  type="text"
                  className="input"
                  value={uploadData.occasionName}
                  onChange={(e) => setUploadData({...uploadData, occasionName: e.target.value})}
                  placeholder="e.g., Diwali Wishes"
                />
              </div>

              <div className="space-y-2">
                <label className="label">Category</label>
                <select
                  className="select"
                  value={uploadData.category}
                  onChange={(e) => setUploadData({...uploadData, category: e.target.value})}
                >
                  <option value="Festival">Festival</option>
                  <option value="Birthday">Birthday</option>
                  <option value="Anniversary">Anniversary</option>
                  <option value="Holiday">Holiday</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="label">Tags (comma separated)</label>
                <input
                  type="text"
                  className="input"
                  value={uploadData.tags}
                  onChange={(e) => setUploadData({...uploadData, tags: e.target.value})}
                  placeholder="diwali, lights, celebration"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-border">
                <button
                  className="btn btn-outline"
                  onClick={() => setShowMediaUpload(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-default"
                  onClick={handleUpload}
                  disabled={uploading || !uploadData.file}
                >
                  {uploading ? 'Uploading...' : 'Upload Media'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FestivalManagement;